/**
 * Created by mauget on 1/25/16.
 */

// CommandGenericView.js

import $                    from 'jquery';
import _                    from 'lodash';
import Immutable            from 'immutable';
import fabric               from 'fabric.js/dist/fabric.require';
import { LabeledRectClass } from 'shapelib/LabeledRectClass'
import { StartClass }       from 'shapelib/StartClass'
import { ExitClass }        from 'shapelib/ExitClass'
import { RectangleWithTextClass }   from 'shapelib/RectangleWithTextClass'
import { CircleWithTextClass }      from 'shapelib/CircleWithTextClass'
import util                 from 'util/Util';

import { View }         from 'backbone';
import constants        from 'Constants';
import listService      from 'ListService';
import focusController  from 'FocusController';
import eventBus         from 'EventBus';

import widgetUtil       from 'util/WidgetUtil';

import AttributesView           from 'properties/AttributesView'
import TabSettingsView          from 'behaviors/properties/TabSettingsView';
import TabSummaryView           from 'behaviors/properties/TabSummaryView';

const SILENT = constants.MODEL_SILENT;

//======================================================================================
// Use an instance of this view to front the most common kind of Behaviors widget.
// -Lou Mauget, January 25, 2016
//======================================================================================
class CommandGenericView extends View {

    initialize( options ) {

        // The layout page. This id is Used to query its bounds.
        this.canvasId = constants.PAGESEL_ID;

        this.options = options || {};

        this.model.set( 'id', this.id, SILENT);

        this.listenTo(this.model, 'change', this.rerender);
        this.listenTo(this.model,'widgetTextUpdated',this.rerender);

        this.widgetConfig = {};
        const widgetWidth = this.model.get('width');
        const widgetHeight = this.model.get('height');

        if(widgetWidth){
            this.widgetConfig['width'] = widgetWidth;
        }
        if(widgetHeight){
            this.widgetConfig['height'] = widgetHeight;
        }

        this.width  = Number(options.width || widgetWidth);
        this.height = Number(options.height || widgetHeight);
        this.hoverCursor = 'pointer';

        return this;
    }

    //===============================================
    // Obliterate all VISUAL traces of this view
    //===============================================
    remove() {
        // Fabric event off wants exact info it saw for even on.:
        const canvas = listService.fcanvas;

        this.undelegateEvents();
        this.stopListening();
        super.remove();
        return this;
    }

    //=============================================================================
    // Make a single connection point at the given (left, top) position
    //=============================================================================
    _createConnectionPoint(dot, left, top, dotRadius ){
        return new fabric.Circle({
            left: left,
            top: top,
            radius: dotRadius,
            fill: constants.CONNECTION_CLR_CLEAR,
            dot: dot
        });
    }


    //============================================================================================================
    // Returns a fabricjs group contained in a rect widget having multiple connection points.
    //
    // According to observed XML created in Flash:
    // - Behavior command widgets have internal connection point (0 : east) (1 : south) 2 : west) (3 : north)
    // - Simple/multiple branches have 8 connection points (0: south, increasing clockwise)
    // - Workflow stages have 12 connection points, three per side! (0 : side-northeast ... 11 : top-northeast)
    // - Comments have no connections
    //============================================================================================================
    _createConnectionableRect(parentWidget, pos ){

        const group = [ parentWidget ]; // 0 base -- sits at index 0 of group; it's not a connection.

        const
            width = parentWidget.getWidth(),
            height = parentWidget.getHeight();

        if ( this.model.get('factoryKey') !== 'command_comment' && this.model.get('factoryKey') !== 'workflow_comment' ) {

            if (this.model.get('factoryKey') === 'workflow_stage') {
                this._generateStageConnectionPoints(pos, group);

            } else if (this.model.get('factoryKey') === 'command_simple_branch' || this.model.get('factoryKey') === 'command_multiple_branch') {
                this._generateBranchConnectionPoints(pos, group);

            } else {
                // Behaviors
                this._generateCommandConnectionPoints(pos, group);
            }
        }

        return group;
    }

    //============================================================================================================
    // Given a FabricJs group array containing a parent widget at the passed postion (pos), push a set of
    // 12 connection points to it.
    //
    // We explain the dots/points positions below, where "Stage n" is the parent workflow widget.
    //
    // Workflow point numbering is a range of 0 .. 11. The top right-hand dot on the east side is the orgin.
    // We number counter-clockwise consecutively, from that dot. A widget stage concept is:
    //
    //           n1   north   n3
    //            9    10     11
    // w1   8  / ----------------\  0 e1
    // west 7 |     Stage n       | 1 E  east
    // w3   6  \ ----------------/  2 e3
    //            5     4      3
    //           s1   south   s3
    //
    // The FabricJS group result array holds the stage widget followed by its sequence of dot shapes.
    // We return the group to the caller, but it's also returned through the parameter reference.
    //============================================================================================================
    _generateStageConnectionPoints(pos, group){

        const
            parentWidget = group[0],
            width = parentWidget.getWidth(),
            height = parentWidget.getHeight();

        const
            dotRadius = constants.CONNECT_PT_RADIUS_WRKFLW,
            hthird = (height - 2 * dotRadius) / 3,
            wthird = (width - 2 * dotRadius) / 3,
            left = pos.left,
            top = pos.top;

        let dotNum = 0;

        // Push workflow dots CCW starting at top of east side
        const east1 = this._createConnectionPoint( dotNum++, left - dotRadius + width / 2, top - dotRadius - hthird, dotRadius);
        const east = this._createConnectionPoint( dotNum++, left - dotRadius + width / 2, top - dotRadius, dotRadius);
        const east3 = this._createConnectionPoint( dotNum++, left - dotRadius + width / 2, top - dotRadius + hthird, dotRadius);

        const south3 = this._createConnectionPoint( dotNum++, left - dotRadius + wthird, top - dotRadius + height / 2, dotRadius);
        const south = this._createConnectionPoint( dotNum++, left - dotRadius,          top - dotRadius + height / 2, dotRadius);
        const south1 = this._createConnectionPoint( dotNum++, left - dotRadius - wthird, top - dotRadius + height / 2, dotRadius);

        const west1 = this._createConnectionPoint( dotNum++, left - dotRadius - width / 2, top - dotRadius+ hthird, dotRadius);
        const west = this._createConnectionPoint( dotNum++, left - dotRadius - width / 2, top - dotRadius, dotRadius);
        const west3 = this._createConnectionPoint( dotNum++, left - dotRadius - width / 2, top - dotRadius - hthird, dotRadius);

        const north1 = this._createConnectionPoint( dotNum++, left - dotRadius - wthird, top - dotRadius - height / 2, dotRadius);
        const north = this._createConnectionPoint( dotNum++, left - dotRadius,          top - dotRadius - height / 2, dotRadius);
        const north3 = this._createConnectionPoint( dotNum++, left - dotRadius + wthird, top - dotRadius - height / 2, dotRadius);

        group.push(east1);
        group.push(east);
        group.push(east3);

        group.push(south3);
        group.push(south);
        group.push(south1);

        group.push(west1);
        group.push(west);
        group.push(west3);

        group.push(north1);
        group.push(north);
        group.push(north3);


        // Parent widget group: {stage, dot 0, ... dot 11]
        return group;
    }


    //============================================================================================================
    // Given a FabricJs group array containing a parent widget at the passed postion (pos), push a set of
    // 4 connection points to it.
    //
    // We explain the dots/points positions below, where "Command n" is the parent behaviors widget.
    //
    // Command point numbering is a range of 0 .. 3. The middle east side is the origin.
    // We number counter-clockwise consecutively, from that dot. A widget command concept is:

    //                north
    //                  3
    //         / ----------------\
    // west 2 |     Coomand n     | 0 E  east
    //         \ ----------------/
    //                  1
    //                south

    // The FabricJS group result array holds the stage widget followed by its sequence of dot shapes.
    // We return the group to the caller, but it's also returned through the parameter reference.
    //============================================================================================================
    _generateCommandConnectionPoints(pos, group) {
        const
            dotRadius = constants.CONNECT_PT_RADIUS,
            parentWidget = group[0],
            width = parentWidget.getWidth(),
            height = parentWidget.getHeight();

        const east = this._createConnectionPoint(constants.BEH_DOT_E, pos.left - dotRadius + width / 2, pos.top - dotRadius, dotRadius);
        const south = this._createConnectionPoint(constants.BEH_DOT_S, pos.left - dotRadius, pos.top - dotRadius + height / 2, dotRadius);
        const west = this._createConnectionPoint(constants.BEH_DOT_W, pos.left - dotRadius - width / 2, pos.top - dotRadius, dotRadius);
        const north = this._createConnectionPoint(constants.BEH_DOT_N, pos.left - dotRadius, pos.top - dotRadius - height / 2, dotRadius);

        group.push(east);
        group.push(south);

        group.push(west);
        group.push(north);

        // Parent widget group: {stage, dot 0, ... dot 3]
        return group;
    }


    _generateBranchConnectionPoints(pos, group ) {
        const
            dotRadius = constants.CONNECT_PT_RADIUS,
            numPoints = 8;

        // PF starting point is East and goes counter-clockwise, we'll have to account for that in the logic

        const r = util.extractNum(this.model.get('width')) / 2;
        let dotNum = 0;

        const
            increment = 360 / numPoints,
            x = pos.left - 7,
            y = pos.top - 7,
            t = 0.03,
            d = 0.90,
            radian = Math.PI / 180;

        // Place a dot/point on the circle circumfernce at degree angle 'a';
        const fnAddPoint = ( a => {
            const
                markX = x + r * Math.cos(radian * a),
                markY = y + r * Math.sin(radian * a);

            const
                xt = ((d - t) * markX + t * x),
                yt = ((d - t) * markY + t * y);

            const point = this._createConnectionPoint(dotNum, markX, markY, dotRadius);
            group.push(point);

            dotNum++;
        });

        for (let a = 90; a > 0; a -= increment) {
            fnAddPoint( a );
        }
        for (let a = 360; a > 90; a -= increment) {
            fnAddPoint( a )
        }
    }

    //===============================================
    // Decorate base shape with connection shapes
    //===============================================
    _decorateWithConnectionPoints( canvas, fabObject, pos, targetIndex = -1, isRender = false, loadingBehaviors=false  ){
        //Delete borders of the last item dropped
        canvas.getObjects().map( obj => {
            if(obj.active){
                obj.active = false;
                obj.hasBorders = false;
            }
        });

        this.options.group =  new fabric.Group( this._createConnectionableRect( fabObject, pos ), { hasRotatingPoint: false, hasControls: false } );
        this.options.group.model = this.model;
        this.options.group.active = true;

        canvas.add( this.options.group );
        if (isRender && !loadingBehaviors){
          canvas.setActiveObject(this.options.group);
        }        
        
        // Simple branch stays low so that its tags remain visible
        const  model = this.options.group.model;
        if ( model && model.get('factoryKey') === 'command_simple_branch' && !isRender) {
            canvas.sendToBack(this.options.group);
        }

        if (targetIndex !== -1){
           listService.fcanvas.moveTo(this.options.group,targetIndex);
        }            

        return this.options.group;
    }

    //=============================================================================
    // Create widget via fabric.js.
    // This is the workhorse shape used by behavior commands and workflow stages.
    // Decorated with live, normally-invisible connection dots.
    //=============================================================================
    drawWidget(pos, name, icon, fillColor, adjustPosition, targetIndex =-1, isRender = false, visible, loadingBehaviors) {

        const
            canvas = listService.fcanvas,
            width = Number(this.model.get('width')),
            height = Number(this.model.get('height'));

        pos.left = constants.REDRAW_OFFSET + Number(pos.left) + width / 2;
        pos.top = constants.REDRAW_OFFSET + Number(pos.top) + height / 2;

        _.merge(this.widgetConfig, {
            left: pos.left,
            top:  pos.top,
            label: name,
            widgetText: this.model.get('widgetText'),
            icon: icon,
            width: width,
            height: height,
            fillColor: fillColor,
            visible: visible
        });

        const fabObject =  new LabeledRectClass( this.widgetConfig );
        return this._decorateWithConnectionPoints(canvas, fabObject, pos, targetIndex, isRender, loadingBehaviors);
    }

    //===============================================
    // Create start shape via facbric.js
    //===============================================
    drawStart(pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible, loadingBehaviors) {

        const
            canvas = listService.fcanvas,
            width = Number(this.model.get('width')),
            height = Number(this.model.get('height'));

        pos.left = Number(pos.left) + width / 2;
        pos.top  = Number(pos.top) + height / 2;

        //if (!adjustPosition) {
            pos.left += constants.REDRAW_OFFSET;
            pos.top += constants.REDRAW_OFFSET;
        //}

        _.merge(this.widgetConfig, {
            left: pos.left,
            top: pos.top,
            label: name,
            widgetText: this.model.get('widgetText'),
            icon: icon,
            width: width,
            height: height,
            fillColor: fillColor,
            visible: visible
        });

        const fabObject = new StartClass( this.widgetConfig );
        return this._decorateWithConnectionPoints(canvas, fabObject, pos, targetIndex, isRender, loadingBehaviors);
    }


    drawExit(pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible, loadingBehaviors) {

        const
            canvas = listService.fcanvas,
            width = Number(this.model.get('width')),
            height = Number(this.model.get('height'));

        pos.left = Number(pos.left) + width / 2;
        pos.top  = Number(pos.top) + height / 2;

        //if (!adjustPosition) {
            pos.left += constants.REDRAW_OFFSET;
            pos.top += constants.REDRAW_OFFSET;
        //}

        _.merge(this.widgetConfig, {
            left: pos.left,
            top: pos.top,
            label: name,
            widgetText: this.model.get('widgetText'),
            icon: icon,
            width: width,
            height: height,
            fillColor: fillColor,
            visible: visible
        });

        const fabObject = new ExitClass( this.widgetConfig );
        return this._decorateWithConnectionPoints(canvas, fabObject, pos, targetIndex, isRender, loadingBehaviors);
    }


    //===============================================
    // Create circle shape via facbric.js
    //===============================================
    drawBranch(pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible, loadingBehaviors) {

        const
            canvas = listService.fcanvas,
            radius = Number(this.model.get('width')) / 2;

        pos.left = constants.REDRAW_OFFSET + Number(pos.left) + radius;
        pos.top = constants.REDRAW_OFFSET + Number(pos.top) + radius;

        _.merge(this.widgetConfig, {
            radius: radius,
            left: pos.left - radius,
            top: pos.top - radius,
            label: name,
            widgetText: this.model.get('widgetText'),
            icon: icon,
            fillColor: fillColor,
            visible: visible
        });

        const fabObject = new CircleWithTextClass( this.widgetConfig );
        return this._decorateWithConnectionPoints(canvas, fabObject, pos, targetIndex, isRender, loadingBehaviors);
    }


    //=========================================================
    // Create rectangle shape via fabric.js
    // Currently only called to make a behavior COMMENT.
    // No connections added.
    //=========================================================
    drawRectangle(pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible, loadingBehaviors) {
        const
            canvas = listService.fcanvas,
            width = util.extractNum(this.model.get('width')),
            height = util.extractNum(this.model.get('height'));

        const options = {
            left: util.extractNum(pos.left) + width / 2,
            top: util.extractNum(pos.top) + height / 2,
            label: ( this.model.get('text') ? this.model.get('text') :(this.model.attributes.factoryKey === 'command_comment' ? ' ' : name)),
            icon: icon,
            width: width,
            height: height,
            fillColor: fillColor,
            visible: visible
        };

        const fabObject =  new RectangleWithTextClass( options);
        // Returns a group of just this object having no connection points. The model knows ..
        return this._decorateWithConnectionPoints(canvas, fabObject, pos, targetIndex, isRender, loadingBehaviors);
    }

    //=======================================================================================================
    // Draws rectangle
    // Only called for COMMENT on behavior workflow  (on 2017-09-30)
    //=======================================================================================================
    drawRectangleWithoutConnection(pos, name, icon, fillColor, visible=false, loadingBehaviors) {
        const canvas = listService.fcanvas;
        let width = Number(this.model.get('width'));
        let height = Number(this.model.get('height'));
        pos.left = Number(pos.left) + width / 2;
        pos.top = Number(pos.top) + height / 2;

        const rect =  new RectangleWithTextClass(  {
            left: pos.left,
            top: pos.top,
            label: this.model.get('text')? this.model.get('text') :name,
            icon: icon,
            width: width,
            height: height,
            fillColor: fillColor,
            visible: visible
        });

        this.options.group =  new fabric.Group( [ rect ], { hasRotatingPoint: false, hasBorders: true } );
        this.options.group.model = this.model;
        canvas.add( this.options.group );
        // PF-3892: Show available edit menu options
        canvas.setActiveObject(this.options.group);

        return this.options.group;
    }

    switchProperties(model) {
        focusController.clearAllSelected();

        listService.objectList.map( view => {
            if ( view.subviews ) {
                view.subviews.map( (propertyView) => {
                    propertyView.undelegateEvents();
                });
            }
        });

        //Circular references
        $(constants.PROP_BHVR_ID).empty();
        this._subviews = Immutable.Map()
            .set( 'attributesView', new AttributesView( { model: model, el: $(constants.PROP_ATTRIBUTES_ID)} ))
            .set( 'summaryView', new TabSummaryView( { model: model, el: $(constants.PROP_TAB_SUMMARY_ID), type: this.options.type} ))
            .set( 'settingsView', new TabSettingsView( { model: model, el: $(constants.PROP_SETTNGS_ID), type: this.options.type} ));

        if (this._subviews) {
            this.delegateEvents();
            this._subviews.map( pview => {
                pview.render();
                pview.delegateEvents();
            });
        }
        $('#settings-tab a').click();

    }

    //======================================================
    // Build the map singleton lazily; stash in static var.
    // If key not found, caller assumes a default.
    //
    // Need a new widget? Code its factory methods;
    // put it in this map under its well-known key.
    //======================================================
    _widgetMap(  ) {
        if (!CommandGenericView.widgetFactoryMap) {
            const mapJS = {};

            mapJS['command_start']              = this.drawStart;
            mapJS['command_exit']               = this.drawExit;
            mapJS['command_simple_branch']      = this.drawBranch;
            mapJS['command_multiple_branch']    = this.drawBranch;
            mapJS['command_comment']            = this.drawRectangle;
            // TODO Reason for  difference should be fixed.
            mapJS['workflow_comment']           = this.drawRectangleWithoutConnection;

            // 'workflow_stage' defaults to generic PF rounded rectangle

            CommandGenericView.widgetFactoryMap = Immutable.Map(mapJS);
        }
        return CommandGenericView.widgetFactoryMap;
    }

    //=================================================
    // Returns a fabric.js widget consisting of
    // a group. If there are connection dots.
    //
    // Item zero is the visible shape
    // fabric object.
    //
    // If there are connection dots, then
    // items one ... three are
    // connection dots -- normally invisible.
    // They're indexed 0..3, clockwise; zero is east.
    //
    // (exception is branches begin at 0 : south)
    //
    // -Lou Mauget August 9, 2016
    //================================================
    _fabricWidgetFactory(pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible=true, loadingBehaviors=false) {
        const
            factoryKey      = this.model.get('factoryKey'),
            factoryMap      = this._widgetMap( factoryKey ),
            defaultFactory  = this.drawWidget,
            factoryFn  = factoryMap.get( factoryKey, defaultFactory),
            widget = factoryFn.call( this, pos, name, icon, fillColor, adjustPosition, targetIndex, isRender, visible, loadingBehaviors) ;
        return widget;
    }

    checkObjectLocation(fcanvas, top, left) {
        // check if out of bounds
        const canvasRawId = constants.CANVAS_RAW_ID;
        const rightEdge = Number(this.model.get('width')) + Number(left);
        const bottomEdge = Number(this.model.get('height')) + Number(top);
        const buffer = 10;
        if ( rightEdge+buffer > fcanvas.width) {
            const newWidth = rightEdge+buffer;
            if (listService.mainKind === 'B') {
                eventBus.trigger('resizeBehaviorCanvas', canvasRawId, newWidth, fcanvas.height);
            }

            if (listService.mainKind === 'W') {
                eventBus.trigger('resizeWorkflowCanvas', canvasRawId, newWidth, fcanvas.height);
            }
        }

        if (bottomEdge+buffer > fcanvas.height) {
            const newHeight = bottomEdge+buffer;

            if (listService.mainKind === 'B') {
                eventBus.trigger('resizeBehaviorCanvas', canvasRawId, fcanvas.width, newHeight);
            }

            if (listService.mainKind === 'W') {
                eventBus.trigger('resizeWorkflowCanvas', canvasRawId, fcanvas.width, newHeight);
            }
        }

    }

    //===============================================
    // Render this view via Fabric.js, not HTML
    //==============================================
    render(loadingBehaviors = false, partialRender=false) {
        if ((listService.mainKind === 'B' && constants.WORKFLOW_FACTORY_KEYS.contains(this.model.get('factoryKey'))) ||
            (listService.mainKind === 'W' && !constants.WORKFLOW_FACTORY_KEYS.contains(this.model.get('factoryKey')))) {
            return;
        }

        // fabric.js renders for us.
        const name = this.model.get( 'name' );
        const icon = this.model.get( 'icon' );
        let fillColor = this.model.get( 'fillColor' ) || constants.SHAPE_FILL_CLR;

        const factoryKey = this.options.factoryKey;
        const id  = this.options.id;
        const fcanvas = listService.fcanvas;

        //Check position of the object, and,if we are on loading behaviors, make it not visible
        //if out of screen boundaries (and also considering scroll factor)
        const pos = {
            left: util.extractNum( this.model.get('x') ),
            top: util.extractNum( this.model.get('y') )
        };

        let addWidth = util.extractNum(this.model.get('width')) / 2,
            addHeight = util.extractNum(this.model.get('height')) / 2;

        let posCop = $('#aPageSel').offset(),
            wX = $('#aForm').scrollLeft(), wY = $('#aForm').scrollTop(),
            wH = $('#aForm').height(), wW = $('#aForm').width(),
            oH = $('#aPageSel').outerHeight(), oW = $('#aPageSel').outerWidth();

        let visible = true;
        if (partialRender && ((pos.left - addWidth) > (wW + wX) || (pos.top - addHeight) > (wH + wY))) {
            visible = false;
        }

        if (this.model.get('factoryKey') === 'workflow_stage' ) {
            if (util.isTrue(this.model.get('isClosed')) ) {
                fillColor = '#acacda';
            }
            if (util.isTrue(this.model.get('isDefault')) ) {
                fillColor = '#b6b6ac';
            }
        }
        this.model.set('widgetText', widgetUtil.getWidgetText(this.model),{silent:true} ) ;
        // populate the name for script property and widget Text too
        if (this.model.get('scriptId')) {
            // lookup behavior name
            const behavior = listService.getBehaviorInstance( listService.cleanId( this.model.get('scriptId') ));
            if (behavior) {
                let bName = behavior.get('name');
                this.model.set('script', bName, {silent: true});
                this.model.set('widgetText', bName, {silent: true});
            }
        }

        if ( this.model.get('factoryKey') === 'command_set_properties' ){
            const id = this.model.get('controlId');
            const obj = listService.getObject( listService.cleanId(id) );
            if (obj) {
                this.model.set(this.fieldDisplayName, obj.model.get('name'), {silent: true});
                this.model.set('widgetText', obj.model.get('name'), {silent: true});
            }
        }

        // check if out of bounds
        this.checkObjectLocation(fcanvas, pos.top, pos.left);

        // We need the widget instqnce in order to process inter-widget connections.
        // It's a fabric.js group consisting of the visible shape and four connection dots.
        // Multi-branch should have 8 connection dots
        // WF stage should have 12 connection dots
        const widget = this._fabricWidgetFactory( pos, name, icon, fillColor, true, -1, true, visible, loadingBehaviors );
        this.model.set('widget', widget, SILENT );
        this.widgetVisible = visible;
        this.renderLeft = pos.left-addWidth;
        this.renderTop = pos.top -addHeight;

//        this.delegateEvents();
        return this;
    }

    rerender() {
        const name = this.model.get('name');
        const icon = this.model.get('icon');
        const id = this.model.get('id');
        const fcanvas = listService.fcanvas;

        if (fcanvas) {

            const target = _(fcanvas.getObjects())
                .filter(w => w.model && w.model.get('id') === id)
                .first();

            //the undo manager can trigger this when the object is no longer on the canvas
            //abort to avoid trying to rerender something is no longer there
            if (!target) { return; }

            // Disable drawing mode on canvas.
            fcanvas.isDrawingMode = false;

            const fillColor = (m => {
                let fillColor = this.model.get('fillColor') || '#CCCCCC';
                if (m.get('factoryKey') === 'workflow_stage') {

                    if (util.isTrue(m.get('isClosed'))) {
                        fillColor = '#acacda';
                    }

                    if (util.isTrue(m.get('isDefault'))) {
                        fillColor = '#b6b6ac';
                    }

                }
                return fillColor;

            })(this.model);


            // cache the connections
            const connections = target.connection;

            //Retrieve current index
            let targetIndex = -1;
            if (target.model) {
                let objs = listService.fcanvas.getObjects();
                targetIndex = _.findIndex(objs, function (o) {
                    if (o.model) {
                        return o.model.id === target.model.get('id');
                    }
                });
            }

            // Remove the widget prior to replacement
            fcanvas.remove(target);

            //const pos = this.options.pos;
            const pos = {
                left: util.extractNum(this.model.get('x')),
                top: util.extractNum(this.model.get('y'))
            };

            const widget = this._fabricWidgetFactory(pos, name, icon, fillColor, true, targetIndex, true, true);
            widget.connection = connections;
            this.model.set('widget', widget, SILENT);
            this.model.set('isNewInstance', true, SILENT);

            // PF-2704 Setting the active object one more time as it gets lost when it is re-rendered.
            fcanvas.setActiveObject(widget);
        }
    }

    unrender() {
        const fcanvas = listService.fcanvas;const target = _( fcanvas.getObjects() )
            .filter( w => w.model && w.model.get('id') === this.model.get('id') )
            .first();
        fcanvas.remove( target );
    }
}

export default CommandGenericView;
